import { Button, Icon, Modal, Stack, Title, useToast } from "@introist/react-foundation/v2";
import { useCallback, useMemo, useState } from "react";
import { SearchInput } from "../../../../components/molecules";
import { Empty, ListView } from "../../../../foundation";
import { ListRow } from "../../../../components/organisms";
import { api } from "../../../../services/rpc/RpcProvider";

export const SharepointFolderSearch = ({
  accountId,
  value,
  onSelect
}: {
  accountId?: string;
  value?: string;
  onSelect: (identifier: string) => unknown;
}) => {
  const toast = useToast();

  const [open, setOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);

  const searchOp = api.connectedAccounts.sharepoint.searchFolder.useMutation();

  const [results, setResults] = useState<any[] | undefined>();

  const title = useMemo(() => {
    if (!value) return undefined;
    const titleParts = value.split("|");
    return titleParts.length > 1 ? titleParts[1] : undefined;
  }, [value]);

  const onSearch = useCallback(async () => {
    setSearching(true);
    await searchOp
      .mutateAsync({ accountId: accountId!, query: search })
      .then(res => setResults(res))
      .catch(() => {
        toast.error("Failed to search Sharepoint folders");
      })
      .finally(() => setSearching(false));
  }, [accountId, search, toast, searchOp, setResults]);

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        {title ?? "Select a folder from Sharepoint"}
      </Button>
      <Modal title="Select a folder from Sharepoint" open={open} onClose={() => setOpen(false)}>
        <Stack vertical style={{ width: "500px", height: "50vh" }}>
          <Stack>
            <SearchInput
              style={{ flex: 1 }}
              value={search}
              onChange={setSearch}
              onKeyUp={e => {
                if (e.key === "Enter") {
                  onSearch();
                }
              }}
            />
            <Button onClick={onSearch} loading={searching}>
              Search
            </Button>
          </Stack>
          {results === undefined && <Empty title="Start by making a search" message={null} />}
          {results !== undefined && (
            <ListView.List
              data={results}
              style={{ overflowY: "auto" }}
              empty={{
                title: "No results found",
                message: "Try refining your search query."
              }}
              renderRow={(item: any) => (
                <ListRow
                  gridTemplateColumns="auto 1fr auto"
                  onClick={() => {
                    setSearch("");
                    onSelect(`${(item as any).driveId}:${item.itemId}|${item.name}`);
                    setOpen(false);
                  }}
                >
                  {<Icon name="folder" />}
                  <Title variant="bold">{item.name}</Title>
                </ListRow>
              )}
            />
          )}
          {results && results.length > 20 && (
            <Title small>
              Notice that the results are limited to the first 25 items returned by Sharepoint. If
              you need to search for more items, please refine your search query.
            </Title>
          )}
        </Stack>
      </Modal>
    </>
  );
};
